import { useState, useEffect } from 'react';
import { useNavigate, Link, useLocation, useSearchParams } from 'react-router-dom';
import { Form, Input, Button, Alert, Checkbox, Radio, Space, Typography } from 'antd';
import {
  UserOutlined,
  LockOutlined,
  BankOutlined,
  EyeOutlined,
  EyeInvisibleOutlined,
} from '@ant-design/icons';
import { useAuthStore } from '@/store/authStore';
import { rememberLoginEntry, sanitizeReturnUrl } from '@/utils/safeReturnUrl';

/** Stores username + remember flag only — never persist passwords (production security). */
const REMEMBER_ME_KEY = 'withu_remember_username';

const { Text } = Typography;

export function normalizeLoginError(err: any): string {
  const status = err?.response?.status;
  const rawMsg = String(err?.response?.data?.message || err?.message || '');
  if (status === 423 || rawMsg.toLowerCase().includes('locked')) {
    return rawMsg || 'Account is temporarily locked. Please try again later.';
  }
  return 'Unable to sign in. Check your credentials and try again.';
}

const LoginPage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const {
    login,
    completeCompanySelection,
    clearPendingCompanySelection,
    pendingCompanySelection,
    error: authError,
    setError: setStoreError,
  } = useAuthStore();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selectedCompanyId, setSelectedCompanyId] = useState<string | undefined>();
  const passwordResetSuccess = Boolean((location.state as { passwordReset?: boolean })?.passwordReset);
  const [form] = Form.useForm();

  useEffect(() => {
    rememberLoginEntry(location.pathname);
  }, [location.pathname]);

  useEffect(() => {
    try {
      const legacyKey = 'withu_remember_credentials';
      const raw = localStorage.getItem(REMEMBER_ME_KEY) ?? localStorage.getItem(legacyKey);
      if (raw) {
        const parsed = JSON.parse(raw) as { username?: string; password?: unknown; remember?: boolean };
        localStorage.removeItem(legacyKey);
        if (parsed.password != null) {
          localStorage.removeItem(REMEMBER_ME_KEY);
          form.setFieldsValue({ remember: false });
          return;
        }
        if (parsed.username) {
          form.setFieldsValue({
            username: parsed.username,
            remember: parsed.remember === true,
          });
        }
      }
    } catch {
      localStorage.removeItem(REMEMBER_ME_KEY);
      localStorage.removeItem('withu_remember_credentials');
    }
  }, [form]);

  useEffect(() => {
    if (pendingCompanySelection?.companies?.length) {
      const parent = pendingCompanySelection.companies.find((c) => c.is_parent);
      setSelectedCompanyId(parent?.company_id || pendingCompanySelection.companies[0].company_id);
    }
  }, [pendingCompanySelection]);

  const goAfterLogin = () => {
    const fromState = (location.state as { from?: string } | null)?.from;
    const dest = sanitizeReturnUrl(searchParams.get('returnUrl') || fromState || '/dashboard');
    navigate(dest, { replace: true });
  };

  const handleClearError = () => {
    setError(null);
    setStoreError(null);
  };

  const onFinish = async (values: { username: string; password: string; remember?: boolean }) => {
    try {
      setLoading(true);
      handleClearError();

      if (values.remember) {
        localStorage.setItem(
          REMEMBER_ME_KEY,
          JSON.stringify({
            username: values.username,
            remember: true,
          })
        );
      } else {
        localStorage.removeItem(REMEMBER_ME_KEY);
      }

      const ok = await login(values.username, values.password);
      if (ok) {
        goAfterLogin();
      }
    } catch (err: any) {
      setError(normalizeLoginError(err));
    } finally {
      setLoading(false);
    }
  };

  const onSelectCompany = async () => {
    if (!selectedCompanyId) {
      setError('Select a company to continue');
      return;
    }
    try {
      setLoading(true);
      handleClearError();
      await completeCompanySelection(selectedCompanyId);
      goAfterLogin();
    } catch (err: any) {
      setError(err?.response?.data?.message || 'Could not open the selected company.');
    } finally {
      setLoading(false);
    }
  };

  const activeErrorMessage = error || (authError ? normalizeLoginError({ message: authError }) : null);

  if (pendingCompanySelection) {
    return (
      <div className="auth-form-surface">
        {activeErrorMessage && (
          <Alert
            className="auth-alert-error"
            message={activeErrorMessage}
            type="error"
            closable
            onClose={handleClearError}
            showIcon
            role="alert"
            style={{ marginBottom: 16 }}
          />
        )}
        <Space direction="vertical" size="middle" style={{ width: '100%' }}>
          <div>
            <Text strong style={{ fontSize: 16 }}>
              <BankOutlined /> Choose company
            </Text>
            <div>
              <Text type="secondary">Your account has access to more than one company.</Text>
            </div>
          </div>
          <Radio.Group
            value={selectedCompanyId}
            onChange={(e) => setSelectedCompanyId(e.target.value)}
            style={{ width: '100%' }}
          >
            <Space direction="vertical" style={{ width: '100%' }}>
              {pendingCompanySelection.companies.map((c) => (
                <Radio key={c.company_id} value={c.company_id} style={{ display: 'block' }}>
                  {c.name} ({c.code})
                  {c.is_parent ? ' — home' : ''}
                </Radio>
              ))}
            </Space>
          </Radio.Group>
          <Button
            type="primary"
            block
            size="large"
            loading={loading}
            disabled={loading}
            onClick={onSelectCompany}
            className="auth-submit-button"
          >
            {loading ? 'Opening company…' : 'Continue'}
          </Button>
          <Button
            type="link"
            block
            disabled={loading}
            onClick={() => {
              clearPendingCompanySelection();
              handleClearError();
            }}
          >
            Back to sign in
          </Button>
        </Space>
      </div>
    );
  }

  return (
    <div className="auth-form-surface">
      {passwordResetSuccess && (
        <Alert
          type="success"
          showIcon
          message="Password updated"
          description="You can sign in with your new password."
          style={{ marginBottom: 16 }}
          closable
        />
      )}
      {activeErrorMessage && (
        <Alert
          className="auth-alert-error"
          message={activeErrorMessage}
          type="error"
          closable
          onClose={handleClearError}
          showIcon
          role="alert"
        />
      )}

      <Form
        form={form}
        name="login"
        className="auth-login-form"
        onFinish={onFinish}
        layout="vertical"
        size="large"
      >
        <Form.Item
          name="username"
          label="Email or username"
          rules={[{ required: true, message: 'Please enter your email or username.' }]}
        >
          <Input
            id="auth-login-username"
            prefix={<UserOutlined aria-hidden />}
            placeholder="name@company.com or username"
            autoComplete="username"
            autoCapitalize="none"
            autoCorrect="off"
            spellCheck={false}
          />
        </Form.Item>

        <Form.Item
          name="password"
          label="Password"
          rules={[{ required: true, message: 'Please enter your password.' }]}
        >
          <Input.Password
            id="auth-login-password"
            prefix={<LockOutlined aria-hidden />}
            placeholder="Enter password"
            autoComplete="current-password"
            enterKeyHint="go"
            iconRender={(visible) =>
              visible ? (
                <EyeOutlined aria-label="Hide password" />
              ) : (
                <EyeInvisibleOutlined aria-label="Show password" />
              )
            }
          />
        </Form.Item>

        <Form.Item className="auth-login-actions-row">
          <div className="auth-login-actions-inner">
            <Form.Item name="remember" valuePropName="checked" noStyle>
              <Checkbox className="auth-remember-checkbox">Remember me</Checkbox>
            </Form.Item>
            <Link to="/forgot-password" className="auth-forgot-link">
              Forgot password?
            </Link>
          </div>
        </Form.Item>

        <Form.Item>
          <Button
            type="primary"
            htmlType="submit"
            block
            size="large"
            loading={loading}
            disabled={loading}
            aria-busy={loading}
            className="auth-submit-button"
          >
            {loading ? 'Signing in…' : 'Sign in'}
          </Button>
        </Form.Item>

        <p className="auth-login-footnote" id="auth-login-footnote">
          Access is managed by your organization. Contact your administrator if you need assistance.
        </p>
      </Form>
    </div>
  );
};

export default LoginPage;
